import { initializeDB, getById, addOrUpdate, getAll } from '../config/db';
import { INCIDENT_STORE, URL } from '../config/constants';

/**
 * @typedef {import('../types/incident').Incident} Incident
 * @typedef {import('../types/incident').IncidentDraft} IncidentDraft
 * @typedef {import('../types/incident').IncidentFormData} IncidentFormData
 * @typedef {import('../types/incident').IncidentStatus} IncidentStatus
 */

/**
 * Generate UUID v4
 * @returns {string}
 */
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Стадии workflow, зеркало App\Entity\Incident::STAGE_*.
 * Приезжают с сервера синком incidentAssignments.
 */
export const INCIDENT_STAGE = {
    DRAFT: 'draft',
    INITIAL: 'initial',
    ASSIGNED: 'assigned',
    COMPLETED: 'completed',
    CLOSED: 'closed'
};

/**
 * Service for managing incident reports in IndexedDB
 */
const incidentService = {
    /**
     * Create a new draft incident
     * @returns {Promise<IncidentDraft>}
     */
    async createDraft() {
        const db = await initializeDB();
        const now = new Date().toISOString();
        
        /** @type {IncidentDraft} */
        const draft = {
            uuid: generateUUID(),
            status: 'processing',
            payload: {
                incidentDateTime: '',
                employees: [],
                witnesses: [],
                location: {
                    type: 'job',
                    location: '',
                    jobId: null
                },
                description: '',
                // Keep legacy single value for compatibility
                incidentType: 'Near Miss/Close Call',
                // New multi-select storage
                incidentTypes: []
            },
            createdAt: now,
            updatedAt: now
        };
        
        const id = await db.add(INCIDENT_STORE, draft);
        draft.id = id;
        
        return draft;
    },
    
    /**
     * Update an existing draft
     * @param {number} id
     * @param {Partial<IncidentFormData>} patch
     * @returns {Promise<void>}
     */
    async updateDraft(id, patch) {
        const db = await initializeDB();
        const incident = await db.get(INCIDENT_STORE, id);
        
        if (!incident) {
            throw new Error(`Incident with id ${id} not found`);
        }
        
        // Merge the patch with existing payload
        incident.payload = {
            ...incident.payload,
            ...patch
        };
        
        incident.updatedAt = new Date().toISOString();
        
        await db.put(INCIDENT_STORE, incident);
    },
    
    /**
     * Update entire incident record
     * @param {number} id
     * @param {Partial<Incident>} updates
     * @returns {Promise<void>}
     */
    async updateIncident(id, updates) {
        const db = await initializeDB();
        const incident = await db.get(INCIDENT_STORE, id);
        
        if (!incident) {
            throw new Error(`Incident with id ${id} not found`);
        }
        
        const updated = {
            ...incident,
            ...updates,
            updatedAt: new Date().toISOString()
        };
        
        await db.put(INCIDENT_STORE, updated);
    },
    
    /**
     * Get all drafts by status
     * @param {IncidentStatus} status
     * @returns {Promise<Incident[]>}
     */
    async listDraftsByStatus(status) {
        const db = await initializeDB();
        const tx = db.transaction(INCIDENT_STORE, 'readonly');
        const store = tx.objectStore(INCIDENT_STORE);
        const index = store.index('status');
        
        const incidents = await index.getAll(status);
        
        // Sort by updatedAt descending
        incidents.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB.getTime() - dateA.getTime();
        });
        
        return incidents;
    },
    
    /**
     * Get incident by ID
     * @param {number} id
     * @returns {Promise<Incident|undefined>}
     */
    async getById(id) {
        return await getById(INCIDENT_STORE, id);
    },
    
    /**
     * Get incident by UUID
     * @param {string} uuid
     * @returns {Promise<Incident|undefined>}
     */
    async getByUUID(uuid) {
        const db = await initializeDB();
        const tx = db.transaction(INCIDENT_STORE, 'readonly');
        const store = tx.objectStore(INCIDENT_STORE);
        const index = store.index('uuid');
        
        return await index.get(uuid);
    },
    
    /**
     * Все инциденты этого устройства: локальные черновики, отправленные и
     * назначенные (последние приезжают синком incidentAssignments).
     * Сортировка — сначала то, что требует действия.
     * @returns {Promise<Incident[]>}
     */
    async listAll() {
        const all = (await getAll(INCIDENT_STORE)) || [];

        const weight = (incident) => {
            if (incidentService.needsMyAction(incident)) return 0;
            if (incident.status === 'processing') return 1;
            return 2;
        };

        return all.sort((a, b) => {
            const byWeight = weight(a) - weight(b);
            if (byWeight !== 0) return byWeight;
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB.getTime() - dateA.getTime();
        });
    },

    /**
     * Назначен ли инцидент текущему пользователю. Сравниваем по employeeId:
     * имена во внешней базе не нормированы, сопоставление уже сделано на сервере.
     * @param {Incident} incident
     * @returns {boolean}
     */
    isAssignedToMe(incident) {
        const myEmployeeId = localStorage.getItem('employeeId');
        if (!myEmployeeId || !incident?.assignedToEmployeeId) return false;
        return String(incident.assignedToEmployeeId) === String(myEmployeeId);
    },

    /**
     * Нужно ли этому пользователю дозаполнить инцидент.
     * @param {Incident} incident
     * @returns {boolean}
     */
    needsMyAction(incident) {
        return incident?.workflowStage === INCIDENT_STAGE.ASSIGNED
            && incidentService.isAssignedToMe(incident);
    },

    /**
     * Удалять можно только свой локальный черновик, не ушедший на сервер.
     * Отправленное и назначенное живёт до закрытия во внешней базе.
     * @param {Incident} incident
     * @returns {boolean}
     */
    canDelete(incident) {
        if (!incident) return false;
        if (incident.origin === 'assignment') return false;
        if (incident.backendId) return false;
        return incident.status === 'processing';
    },

    /**
     * Как показать инцидент в списке: подпись, цвет чипа и нужно ли действие.
     * @param {Incident} incident
     * @returns {{label: string, color: string, actionRequired: boolean, hint: string}}
     */
    getUiState(incident) {
        const stage = incident?.workflowStage;

        if (incident?.status === 'processing' && !incident?.backendId) {
            return { label: 'Draft', color: 'default', actionRequired: false, hint: 'Not submitted yet' };
        }

        // Отправлено в офлайне: локально уже submitted, но на сервер ещё не ушло.
        if (!incident?.backendId && incident?.origin !== 'assignment' && !stage) {
            return { label: 'Pending sync', color: 'default', actionRequired: false, hint: 'Will be sent when online' };
        }

        if (stage === INCIDENT_STAGE.ASSIGNED) {
            if (incidentService.isAssignedToMe(incident)) {
                return {
                    label: 'Action required',
                    color: 'error',
                    actionRequired: true,
                    hint: 'Assigned to you — complete the report'
                };
            }
            return {
                label: 'Assigned',
                color: 'info',
                actionRequired: false,
                hint: `Assigned to ${incident.assignedToName || 'another employee'}`
            };
        }

        if (stage === INCIDENT_STAGE.COMPLETED) {
            return {
                label: 'Report submitted',
                color: 'success',
                actionRequired: false,
                hint: incident.completedByName
                    ? `Completed by ${incident.completedByName}`
                    : 'Waiting to be closed'
            };
        }

        return {
            label: 'Submitted',
            color: 'warning',
            actionRequired: false,
            hint: 'Waiting to be assigned'
        };
    },

    /**
     * Delete an incident. Отправленное и назначенное не удаляем — правило
     * дублируется здесь, чтобы его нельзя было обойти мимо списка.
     * @param {number} id
     * @returns {Promise<void>}
     */
    async deleteIncident(id) {
        const db = await initializeDB();
        const incident = await db.get(INCIDENT_STORE, id);

        if (incident && !incidentService.canDelete(incident)) {
            const error = new Error('This incident has been submitted and cannot be deleted.');
            error.code = 'DELETE_NOT_ALLOWED';
            throw error;
        }

        await db.delete(INCIDENT_STORE, id);
    },

    /**
     * Submit an incident (change status to submitted)
     * @param {number} id
     * @returns {Promise<void>}
     */
    async submitIncident(id) {
        await this.updateIncident(id, {
            status: 'submitted',
            submittedAt: new Date().toISOString()
        });
    },
    
    /**
     * Get all incidents
     * @returns {Promise<Incident[]>}
     */
    async getAllIncidents() {
        return await getAll(INCIDENT_STORE);
    },

    /**
     * Submit incident to API or queue for offline sync
     * @param {number} id - Incident ID
     * @param {Object} data - Complete incident data
     * @returns {Promise<void>}
     */
    async submitIncident(id, data) {
        const db = await initializeDB();
        const incident = await db.get(INCIDENT_STORE, id);
        
        if (!incident) {
            throw new Error(`Incident with id ${id} not found`);
        }

        // Check if online
        const isOnline = navigator.onLine;
        const types = Array.isArray(data.incidentTypes) ? data.incidentTypes : (data.incidentType ? [data.incidentType] : []);
        const legacyType = types.length > 0 ? types[0] : (data.incidentType || 'Near Miss/Close Call');
        
        if (isOnline) {
            try {
                // Try to submit to API
                const token = localStorage.getItem('token');
                const response = await fetch(`${URL}/incidents`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...data,
                        uuid: incident.uuid,
                        // Send both single and multi fields for backend compatibility
                        incidentType: legacyType,
                        incidentTypes: types,
                        status: 'step1_submitted'
                    })
                });

                if (response.ok) {
                    const result = await response.json();
                    // Save backend ID for later use
                    incident.backendId = result.id;
                    // Update status to step1_submitted for initial report
                    incident.status = 'step1_submitted';
                    // Дальше инцидент ведёт назначенный — до синка показываем
                    // «ждёт назначения», не давая репортёру ничего дозаполнять.
                    incident.workflowStage = INCIDENT_STAGE.INITIAL;
                    incident.submittedAt = new Date().toISOString();
                    incident.payload = { ...data, incidentType: legacyType, incidentTypes: types };
                    await db.put(INCIDENT_STORE, incident);
                    
                    // Create witness forms if needed
                    if (data.witnesses && data.witnesses.length > 0) {
                        await this.createWitnessForms(incident.id, data.witnesses);
                    }
                } else {
                    throw new Error('Failed to submit to API');
                }
            } catch (error) {
                console.error('Error submitting online:', error);
                // Queue for offline sync
                await this.queueForSync(id, data);
            }
        } else {
            // Queue for offline sync
            await this.queueForSync(id, data);
        }
    },

    /**
     * Queue incident for offline sync
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<void>}
     */
    async queueForSync(id, data) {
        const db = await initializeDB();
        const incident = await db.get(INCIDENT_STORE, id);
        
        if (incident) {
            incident.status = 'step1_submitted';
            const types = Array.isArray(data.incidentTypes) ? data.incidentTypes : (data.incidentType ? [data.incidentType] : []);
            const legacyType = types.length > 0 ? types[0] : (data.incidentType || 'Near Miss/Close Call');
            incident.payload = { ...data, incidentType: legacyType, incidentTypes: types };
            incident.updatedAt = new Date().toISOString();
            await db.put(INCIDENT_STORE, incident);
        }
    },

    /**
     * Sync pending incidents when online
     * @returns {Promise<void>}
     */
    async syncPendingIncidents() {
        const db = await initializeDB();
        const tx = db.transaction(INCIDENT_STORE, 'readonly');
        const store = tx.objectStore(INCIDENT_STORE);
        const statusIndex = store.index('status');
        // Get both pending and step1_submitted incidents that need syncing
        const pendingIncidents = await statusIndex.getAll('pending');
        const step1Incidents = await statusIndex.getAll('step1_submitted');
        const allIncidentsToSync = [...pendingIncidents, ...step1Incidents.filter(i => !i.backendId)];
        
        for (const incident of allIncidentsToSync) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${URL}/incidents`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...incident.payload,
                        uuid: incident.uuid,
                        status: incident.status // Keep the original status (step1_submitted)
                    })
                });

                if (response.ok) {
                    const result = await response.json();
                    // Save backend ID for later use
                    incident.backendId = result.id;
                    // Keep the original status
                    incident.workflowStage = incident.workflowStage || INCIDENT_STAGE.INITIAL;
                    incident.submittedAt = new Date().toISOString();
                    
                    const writeTx = db.transaction(INCIDENT_STORE, 'readwrite');
                    const writeStore = writeTx.objectStore(INCIDENT_STORE);
                    await writeStore.put(incident);
                    await writeTx.done;
                }
            } catch (error) {
                console.error('Error syncing incident:', incident.id, error);
            }
        }
    },

    /**
     * Submit full report (after initial report)
     * @param {number} id - Incident ID
     * @param {Object} data - Complete incident data with full report
     * @returns {Promise<void>}
     */
    async submitFullReport(id, data) {
        const db = await initializeDB();
        const incident = await db.get(INCIDENT_STORE, id);
        
        if (!incident) {
            throw new Error(`Incident with id ${id} not found`);
        }

        // Check if online
        const isOnline = navigator.onLine;
        
        if (isOnline) {
            try {
                // Use PUT to update existing incident using UUID
                const token = localStorage.getItem('token');
                const response = await fetch(`${URL}/incidents/${incident.uuid}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...data,
                        status: 'fullReportSubmitted'
                    })
                });

                if (response.ok) {
                    // Update status to fullReportSubmitted
                    incident.status = 'fullReportSubmitted';
                    // Пакет сдан — ждём dateClosed из внешней базы, после него
                    // синк удалит запись из списка.
                    incident.workflowStage = INCIDENT_STAGE.COMPLETED;
                    incident.fullReportSubmittedAt = new Date().toISOString();
                    // Persist auto accident data explicitly so frontend picks it up
                    if (data.autoAccidentData) {
                        incident.autoAccidentData = data.autoAccidentData;
                    }
                    // Merge payload to preserve existing fields
                    incident.payload = { ...(incident.payload || {}), ...data };
                    incident.updatedAt = new Date().toISOString();
                    incident.needsFullReportSync = false;
                    incident.fullReportSyncedAt = new Date().toISOString();
                    await db.put(INCIDENT_STORE, incident);
                    return;
                }

                // 403 — инцидент закрыт или назначен другому. Повторять бессмысленно,
                // в офлайн-очередь такое класть нельзя: она будет долбить вечно.
                if (response.status === 403) {
                    let message = 'This incident is no longer available for editing.';
                    try {
                        const body = await response.json();
                        if (body?.message) message = body.message;
                    } catch (e) { /* тело может быть пустым */ }

                    const rejected = new Error(message);
                    rejected.code = 'SUBMIT_REJECTED';
                    throw rejected;
                }

                // Fall through to offline queue branch when response not ok
                throw new Error('Failed to submit full report to API');
            } catch (error) {
                if (error.code === 'SUBMIT_REJECTED') {
                    throw error;
                }
                console.error('Error submitting full report online:', error);
            }
        }

        // Store payload for later sync without changing status preemptively
        if (data.autoAccidentData) {
            incident.autoAccidentData = data.autoAccidentData;
        }
        incident.payload = { ...(incident.payload || {}), ...data };
        incident.updatedAt = new Date().toISOString();
        incident.needsFullReportSync = true;
        await db.put(INCIDENT_STORE, incident);

        const error = new Error('Full report saved locally; will sync when online.');
        error.code = 'OFFLINE_SYNC_QUEUED';
        throw error;
    },

    /**
     * Sync queued full reports (status 'fullReportSubmitted' but not synced yet)
     */
    async syncQueuedFullReports() {
        const db = await initializeDB();
        const all = await getAll(INCIDENT_STORE);
        const queued = (all || []).filter(i => i && i.needsFullReportSync);
        for (const incident of queued) {
            try {
                const token = localStorage.getItem('token');
                const payload = incident.payload || {};
                const res = await fetch(`${URL}/incidents/${incident.uuid}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...payload,
                        autoAccidentData: incident.autoAccidentData || payload.autoAccidentData || {},
                        status: 'fullReportSubmitted'
                    })
                });
                if (res.ok) {
                    incident.status = 'fullReportSubmitted';
                    incident.workflowStage = INCIDENT_STAGE.COMPLETED;
                    incident.fullReportSubmittedAt = incident.fullReportSubmittedAt || new Date().toISOString();
                    incident.needsFullReportSync = false;
                    incident.fullReportSyncedAt = new Date().toISOString();
                    await db.put(INCIDENT_STORE, incident);
                } else if (res.status === 403) {
                    // Инцидент успели закрыть или переназначить, пока отправка
                    // лежала в очереди. Снимаем флаг — иначе очередь будет
                    // повторять этот запрос на каждом синке до конца времён.
                    incident.needsFullReportSync = false;
                    incident.submitRejectedAt = new Date().toISOString();
                    await db.put(INCIDENT_STORE, incident);
                    console.warn('Queued full report rejected (403) for incident', incident.uuid);
                }
            } catch (e) {
                console.error('Error syncing full report for incident', incident.id, e);
            }
        }
    },

    /**
     * Create witness forms for an incident
     * @param {number} incidentId
     * @param {Array} witnesses
     * @returns {Promise<void>}
     */
    async createWitnessForms(incidentId, witnesses) {
        // This will be implemented when witness forms are created
        console.log('Creating witness forms for incident:', incidentId, witnesses);
    },
    
    /**
     * Validate incident data before submission
     * @param {IncidentFormData} data
     * @returns {{valid: boolean, errors: string[]}}
     */
    validateIncident(data) {
        const errors = [];
        
        if (!data.incidentDateTime) {
            errors.push('Incident date and time is required');
        } else {
            const incidentDate = new Date(data.incidentDateTime);
            if (incidentDate > new Date()) {
                errors.push('Incident date cannot be in the future');
            }
        }
        
        if (!data.employees || data.employees.length === 0) {
            errors.push('At least one employee must be selected');
        }
        
        if (!data.location || !data.location.location) {
            errors.push('Location is required');
        }
        
        if (!data.description || data.description.length < 10) {
            errors.push('Description must be at least 10 characters');
        }
        
        // Validate types: allow either single or multi to be provided
        const types = Array.isArray(data.incidentTypes) ? data.incidentTypes : [];
        if ((!data.incidentType || data.incidentType.length === 0) && types.length === 0) {
            errors.push('At least one incident type is required');
        }
        
        return {
            valid: errors.length === 0,
            errors
        };
    }
};

export default incidentService;
